/**
 * FILE TO ONLY BE USED IN MIDDLEWARE
 * Middleware uses edge runtime and does not support some Node.js modules
 * Keep imports in here direct and common, build pipeline should fail if anything is not supported
 */
import { IncomingMessage } from 'http'
import { NextRequest } from 'next/server'
import { NextApiRequest } from 'next'
import {
  getOpCoFromRequest,
  getPlatformHostFromRequest,
  getPlatformBaseUrlFromRequest,
} from '@reward-platform/platform-config/server'
import { getPlatformTypeForHost } from '@reward-platform/platform-config'
import { HEADER_KEYS } from '@reward-platform/shared-api/headers'
import edgeLogger from '@reward-platform/logger/edge'

import { NEXT_API_PATH } from '~/utils/apiConfig'

type NextRequestParameter = IncomingMessage | NextApiRequest | NextRequest

const isEdgeRequest = (req: NextRequestParameter): req is NextRequest => {
  return typeof req.headers?.get === 'function'
}

export const normaliseNextRequest = (req: NextRequestParameter): IncomingMessage => {
  if (isEdgeRequest(req)) {
    // It's a NextRequest (Edge Middleware)
    const headers: Record<string, string | undefined> = {}
    req.headers.forEach((value, key) => {
      headers[key] = value
    })

    return {
      url: req.url,
      method: req.method.toUpperCase() ?? 'GET',
      headers,
    } as IncomingMessage
  }

  return req
}

export const createCommonHeaders = (req: NextRequest) => {
  const requestHeaders = new Headers(req.headers)

  const httpRequest = normaliseNextRequest(req)
  const opCo = getOpCoFromRequest(httpRequest)
  requestHeaders.set(HEADER_KEYS.partnerCode, opCo)

  const host = getPlatformHostFromRequest(httpRequest)
  const platformType = getPlatformTypeForHost(host)
  requestHeaders.set(HEADER_KEYS.platformType, platformType)

  const apiBaseUrl = getPlatformBaseUrlFromRequest(httpRequest) + NEXT_API_PATH
  requestHeaders.set(HEADER_KEYS.apiCallbackUrl, apiBaseUrl)

  return new Headers(requestHeaders)
}

export const generateNonce = (): string => {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
  return Array.from(array)
    .map((byte) => base64Chars[byte % base64Chars.length])
    .join('')
}

const getReportUri = () => {
  const ddClientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
  const ddService = process.env.NEXT_PUBLIC_DD_SERVICE
  const deployment = process.env.NEXT_PUBLIC_DEPLOYMENT

  const params =
    ddClientToken && ddService && deployment
      ? new URLSearchParams({
          'dd-api-key': ddClientToken,
          'dd-evp-origin': 'content-security-policy',
          ddsource: 'csp-report',
          ddtags: `service:${ddService},env:${deployment}`,
        })
      : undefined

  return params ? `https://browser-intake-datadoghq.eu/api/v2/logs?${params.toString()}` : ''
}

export const cspJsonToHeaderString = (csp: Record<string, string | string[]>, nonce: string) => {
  const reportUri = getReportUri()

  return Object.entries(csp)
    .map(([key, value]) => `${key} ${Array.isArray(value) ? value.join(' ') : value}`)
    .join('; ')
    .replaceAll('NONCE_PLACEHOLDER', nonce)
    .replaceAll('REPORT_URI_PLACEHOLDER', reportUri)
}

export const parseCspFromEnv = (nonce: string): string | undefined => {
  try {
    const cspEnv = process.env.CONTENT_SECURITY_POLICY
    if (!cspEnv) {
      edgeLogger.warn('CONTENT_SECURITY_POLICY environment variable not found', {})
      return undefined
    }
    const cspJson = JSON.parse(cspEnv)

    if (!Object.keys(cspJson).length) {
      edgeLogger.warn('CONTENT_SECURITY_POLICY environment variable is empty', {})
      return undefined
    }

    return cspJsonToHeaderString(cspJson, nonce)
  } catch (error) {
    edgeLogger.error(
      'Failed to parse CSP configuration from environment variable CONTENT_SECURITY_POLICY',
      { error }
    )
    return undefined
  }
}

export const isPaymentPage = (pathname: string) => {
  return /\/orders\/payment\/?$/.test(pathname)
}

export const isOrdersPage = (pathname: string) => {
  return /\/orders(\/|$)/.test(pathname)
}
